import { Alert, Badge, Checkbox, Code, Group, Stack, Text, TextInput } from '@mantine/core';
import { useId, useState } from 'react';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import {
  useRecordAcceptanceArtifactReview,
  useReleaseAcceptanceCampaignCaseHistory,
  useReleaseAcceptanceCampaignStatus,
} from '@/hooks/api/automations/useExecutionCompositions.ts';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';
import type {
  ReleaseAcceptanceCampaignCase,
  ReleaseAcceptanceCampaignCaseRun,
  ReleaseAcceptanceCampaignDeliverableStatus,
  ReleaseAcceptanceCampaignStatus,
  ReleaseDeliveryAssessment,
} from '@/types/automationReleases.ts';
import CompositionRunLink from './CompositionRunLink.tsx';
import { ExactDetails } from './CompositionPresentation.tsx';

const campaignColors: Record<ReleaseAcceptanceCampaignStatus, string> = {
  not_configured: 'gray',
  incomplete: 'orange',
  running: 'orange',
  failed: 'red',
  technically_complete: 'green',
  human_review_pending: 'orange',
  accepted: 'green',
};
const caseColors: Record<ReleaseAcceptanceCampaignCase['status'], string> = {
  missing: 'gray',
  running: 'orange',
  failed: 'red',
  stale: 'orange',
  incomplete: 'orange',
  technically_satisfied: 'green',
  human_review_pending: 'orange',
  accepted: 'green',
};

function readable(value: string) {
  return value.replaceAll('_', ' ');
}

function ArtifactReview({
  releaseId,
  caseId,
  deliverable,
  canEdit,
  onChanged,
}: {
  releaseId: string;
  caseId: string;
  deliverable: ReleaseAcceptanceCampaignDeliverableStatus;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const mutation = useRecordAcceptanceArtifactReview();
  const [evidenceRef, setEvidenceRef] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState('');
  const review = deliverable.human_review;
  const canSubmit =
    canEdit &&
    review.required &&
    review.eligible &&
    review.case_run_id !== null &&
    evidenceRef.trim().length > 0 &&
    acknowledged &&
    !mutation.isPending;
  const submit = async (status: 'accepted' | 'rejected') => {
    if (!canSubmit || review.case_run_id === null) return;
    setError('');
    try {
      await mutation.mutateAsync({
        releaseId,
        request: {
          case_id: caseId,
          deliverable_id: deliverable.deliverable_id,
          case_run_id: review.case_run_id,
          ...(review.evaluation_id != null ? { evaluation_id: review.evaluation_id } : {}),
          status,
          evidence_ref: evidenceRef.trim(),
        },
      });
      setEvidenceRef('');
      setAcknowledged(false);
      onChanged();
    } catch (cause) {
      setError(getHttpErrorMessage(cause));
      onChanged();
    }
  };

  if (!review.required) return null;
  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        Human artifact review
      </Text>
      {review.latest_status && (
        <Text size="sm">
          Latest decision: <Code>{readable(review.latest_status)}</Code>
          {review.reviewer && <> · reviewer {review.reviewer}</>}
          {review.evidence_ref && <> · evidence {review.evidence_ref}</>}
          {review.recorded_at && <> · {new Date(review.recorded_at).toLocaleString()}</>}
        </Text>
      )}
      {!review.eligible && (
        <Alert color="gray">{review.reason ?? 'Review is not yet eligible.'}</Alert>
      )}
      {canEdit && review.eligible && (
        <>
          <TextInput
            label="Review evidence reference"
            description="For example, a ticket, review note, or customer confirmation."
            value={evidenceRef}
            onChange={(event) => setEvidenceRef(event.currentTarget.value)}
            maxLength={255}
          />
          <Checkbox
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.currentTarget.checked)}
            label="I reviewed this exact produced artifact and campaign case run."
          />
          <Group>
            <StyledButton
              variant="outline"
              disabled={!canSubmit}
              loading={mutation.isPending}
              onClick={() => void submit('accepted')}
            >
              Accept artifact
            </StyledButton>
            <StyledButton
              variant="delete"
              disabled={!canSubmit}
              loading={mutation.isPending}
              onClick={() => void submit('rejected')}
            >
              Reject artifact
            </StyledButton>
          </Group>
        </>
      )}
      {error && <Alert color="red">{error}</Alert>}
    </Stack>
  );
}

function DeliverableEvidence({
  releaseId,
  caseId,
  deliverable,
  canEdit = false,
  onChanged = () => undefined,
  allowReview,
}: {
  releaseId: string;
  caseId: string;
  deliverable: ReleaseAcceptanceCampaignDeliverableStatus;
  canEdit?: boolean;
  onChanged?: () => void;
  allowReview: boolean;
}) {
  return (
    <Stack gap="xs" py="xs">
      <Group gap="xs">
        <Text size="sm" fw={500}>
          {deliverable.deliverable_id}
        </Text>
        <Badge variant="outline" color="gray">
          expected {deliverable.expected_outcome}
        </Badge>
        <Badge color={deliverable.qualification_status === 'failed' ? 'red' : 'gray'}>
          {deliverable.qualification_status ?? 'not qualified'}
        </Badge>
        {deliverable.comparison_result && (
          <Badge color={deliverable.comparison_matches_expected ? 'green' : 'orange'}>
            {readable(deliverable.comparison_result)}
          </Badge>
        )}
      </Group>
      <Text size="sm">
        Observed output: <Code>{deliverable.observed_output_state ?? 'not observed'}</Code>
      </Text>
      {deliverable.explanation && <Text size="sm">{deliverable.explanation}</Text>}
      {deliverable.proof_request && (
        <Stack gap={2}>
          <Text size="sm" fw={500}>
            Comparison request: {readable(deliverable.proof_request.state)}
          </Text>
          {deliverable.proof_request.state !== 'completed' && (
            <Text size="sm">
              Phase: {readable(deliverable.proof_request.phase)}. Comparison is not yet
              complete.
            </Text>
          )}
          {deliverable.proof_request.failure && (
            <Alert color="orange">
              {deliverable.proof_request.failure.message}
              {' '}Reason: {deliverable.proof_request.failure.code}.
              {' '}Inspect this exact proof request in Forge before recovery.
            </Alert>
          )}
          <ExactDetails
            summary="Comparison request identity"
            values={{ 'Comparison request ID': deliverable.proof_request.operation_id }}
          />
        </Stack>
      )}
      {deliverable.native_comparison && (
        <Stack gap={2}>
          <Text size="sm" fw={500}>
            Historical workbook comparison
          </Text>
          {Object.entries(deliverable.native_comparison.dimensions).map(([dimension, status]) => (
            <Text size="sm" key={dimension}>
              {readable(dimension)}: {readable(status)}
            </Text>
          ))}
        </Stack>
      )}
      {(deliverable.dataset_id || deliverable.artifact_sha256) && (
        <ExactDetails
          summary="Artifact identity"
          values={{
            'Artifact ID': deliverable.dataset_id,
            'Artifact SHA-256': deliverable.artifact_sha256,
          }}
        />
      )}
      {allowReview && (
        <ArtifactReview
          key={JSON.stringify([
            releaseId,
            caseId,
            deliverable.deliverable_id,
            deliverable.human_review.case_run_id,
            deliverable.human_review.evaluation_id ?? null,
            deliverable.dataset_id,
            deliverable.artifact_sha256,
            deliverable.native_comparison?.comparison_id ?? null,
            deliverable.proof_request?.operation_id ?? null,
          ])}
          releaseId={releaseId}
          caseId={caseId}
          deliverable={deliverable}
          canEdit={canEdit}
          onChanged={onChanged}
        />
      )}
    </Stack>
  );
}

function CaseRunHistory({
  releaseId,
  caseItem,
}: {
  releaseId: string;
  caseItem: ReleaseAcceptanceCampaignCase;
}) {
  const history = useReleaseAcceptanceCampaignCaseHistory(releaseId, caseItem.case_id, true);
  const runs = history.data?.pages.flatMap((page) => page.runs) ?? [];
  return (
    <Stack gap="xs">
      {history.isError && (
        <Alert color="red">
          Campaign case history is unavailable. Refresh the Release evidence.
        </Alert>
      )}
      {!history.data && !history.isError && <Text size="sm">Loading immutable case history…</Text>}
      {runs.map((run) => (
        <CaseRunEvidence
          key={run.case_run_id}
          releaseId={releaseId}
          caseId={caseItem.case_id}
          run={run}
        />
      ))}
      {history.hasNextPage && (
        <StyledButton
          variant="subtle"
          disabled={history.isFetchingNextPage}
          loading={history.isFetchingNextPage}
          onClick={() => void history.fetchNextPage()}
        >
          Load older runs
        </StyledButton>
      )}
    </Stack>
  );
}

function CaseRunEvidence({
  releaseId,
  caseId,
  run,
}: {
  releaseId: string;
  caseId: string;
  run: ReleaseAcceptanceCampaignCaseRun;
}) {
  return (
    <Stack gap="xs" pl="sm">
      <Text size="sm">
        {new Date(run.created_at).toLocaleString()} · <Code>{run.run_status}</Code>
      </Text>
      <CompositionRunLink runId={run.builder_run_id}>
        Inspect exact Builder Run {run.builder_run_id}
      </CompositionRunLink>
      <ExactDetails
        summary="Immutable case-run identity"
        values={{
          'Campaign case run ID': run.case_run_id,
          'Case digest': run.case_digest,
          'Composition digest': run.composition_digest,
          'Resource digest': run.resource_digest,
          'Plan digest': run.plan_digest,
        }}
      />
      {run.deliverables.map((deliverable) => (
        <DeliverableEvidence
          key={deliverable.deliverable_id}
          releaseId={releaseId}
          caseId={caseId}
          deliverable={deliverable}
          allowReview={false}
        />
      ))}
    </Stack>
  );
}

function AcceptanceCase({
  releaseId,
  caseItem,
  canEdit,
  onChanged,
}: {
  releaseId: string;
  caseItem: ReleaseAcceptanceCampaignCase;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [detailsVisible, setDetailsVisible] = useState(false);
  const detailsId = useId();
  return (
    <Stack gap="xs" py="sm">
      <Group gap="xs">
        <Text fw={600}>{caseItem.name}</Text>
        <Badge variant="outline" color="gray">
          {caseItem.scenario_classification}
        </Badge>
        <Badge color={caseColors[caseItem.status]}>{readable(caseItem.status)}</Badge>
      </Group>
      <StyledButton
        variant="subtle"
        aria-expanded={detailsVisible}
        aria-controls={detailsId}
        onClick={() => setDetailsVisible((value) => !value)}
      >
        {detailsVisible ? 'Hide case evidence' : 'Show case evidence'}
      </StyledButton>
      {detailsVisible && (
        <Stack id={detailsId} gap="xs">
          {caseItem.run_id && (
            <CompositionRunLink runId={caseItem.run_id}>
              Inspect current Builder Run {caseItem.run_id}
            </CompositionRunLink>
          )}
          {caseItem.evaluation && (
            <ExactDetails
              summary="Selected evaluation identity"
              values={{
                'Evaluation ID': caseItem.evaluation.evaluation_id,
                'Source case run ID': caseItem.evaluation.source_case_run_id,
                'Source run ID': caseItem.evaluation.source_run_id,
                'Execution ID': caseItem.evaluation.execution_id,
              }}
            />
          )}
          {caseItem.assertion_evidence_observation &&
            caseItem.assertion_evidence_observation.reason_code !== 'current' && (
              <Alert color="orange" title="Assertion evidence needs attention">
                <Text size="sm">{caseItem.assertion_evidence_observation.message}</Text>
                <Text size="sm">{caseItem.assertion_evidence_observation.recovery_message}</Text>
              </Alert>
            )}
          {caseItem.deliverables.map((deliverable) => (
            <DeliverableEvidence
              key={deliverable.deliverable_id}
              releaseId={releaseId}
              caseId={caseItem.case_id}
              deliverable={deliverable}
              canEdit={canEdit}
              onChanged={onChanged}
              allowReview
            />
          ))}
          <CaseRunHistory releaseId={releaseId} caseItem={caseItem} />
        </Stack>
      )}
    </Stack>
  );
}

export default function AcceptanceCampaignEvidence({
  summary,
  ...props
}: {
  releaseId: string;
  canEdit: boolean;
  onChanged: () => void;
  summary?: ReleaseDeliveryAssessment['acceptance_campaign'];
}) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  // Standalone callers retain the detailed view. The Release summary already
  // has the campaign observation, so fetch rows only when requested.
  if (!summary) return <AcceptanceCampaignDetails {...props} />;
  return (
    <Stack gap="xs">
      <StyledButton
        variant="subtle"
        aria-expanded={expanded}
        aria-controls={detailsId}
        onClick={() => setExpanded((value) => !value)}
      >
        {expanded ? 'Hide acceptance case details' : 'Show acceptance case details'}
      </StyledButton>
      {expanded && (
        <div id={detailsId}>
          <AcceptanceCampaignDetails {...props} expected={summary} />
        </div>
      )}
    </Stack>
  );
}

function AcceptanceCampaignDetails({
  releaseId,
  canEdit,
  onChanged,
  expected,
}: {
  releaseId: string;
  canEdit: boolean;
  onChanged: () => void;
  expected?: ReleaseDeliveryAssessment['acceptance_campaign'];
}) {
  const campaign = useReleaseAcceptanceCampaignStatus(releaseId);
  if (campaign.isError)
    return (
      <Alert color="red">
        Acceptance campaign evidence is unavailable.{' '}
        <StyledButton variant="subtle" onClick={() => void campaign.refetch()}>
          Refresh acceptance evidence
        </StyledButton>
      </Alert>
    );
  if (!campaign.data || !campaign.isFetchedAfterMount)
    return <Text size="sm">Loading acceptance campaign evidence…</Text>;
  const evidence = campaign.data;
  const hasCampaignIdentity = expected?.campaign_id != null;
  if (
    expected &&
    ((expected.status === 'not_configured' && evidence.status !== 'not_configured') ||
      (hasCampaignIdentity &&
        (expected.campaign_id !== evidence.campaign_id ||
          (expected.selection_generation != null &&
            expected.selection_generation !== evidence.selection_generation))))
  )
    return (
      <Alert color="orange">
        Campaign selection changed. Refresh the Release observation before reviewing cases.
        <StyledButton
          variant="subtle"
          onClick={() => {
            void campaign.refetch();
            onChanged();
          }}
        >
          Refresh Release observation
        </StyledButton>
      </Alert>
    );
  if (evidence.status === 'not_configured')
    return (
      <Alert color="gray">
        No acceptance campaign is configured for this Release. Campaign authoring and execution
        remain in Forge.
      </Alert>
    );
  return (
    <Stack gap="xs">
      {expected &&
        (!hasCampaignIdentity || expected.selection_generation == null) && (
          <Alert color="gray">
            The summary has no complete campaign selection identity. These details are a separate
            current observation.
          </Alert>
        )}
      <Group>
        <Text fw={600}>Acceptance campaign</Text>
        <Badge color={campaign.isFetching ? 'blue' : campaignColors[evidence.status]}>
          {campaign.isFetching ? 'refreshing' : readable(evidence.status)}
        </Badge>
        <Text size="sm" c="dimmed">
          Customer acceptance is separate from lifecycle and publication.
        </Text>
      </Group>
      {Object.entries(evidence.counts).map(([status, count]) => (
        <Text key={status} size="sm">
          {count} {readable(status)}
        </Text>
      ))}
      {evidence.cases.map((caseItem) => (
        <AcceptanceCase
          key={caseItem.case_id}
          releaseId={releaseId}
          caseItem={caseItem}
          canEdit={canEdit}
          onChanged={() => {
            void campaign.refetch();
            onChanged();
          }}
        />
      ))}
    </Stack>
  );
}
